import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import * as fromFloorPlanEditor from '../reducers';
import {
  FloorPlanEditorViewActionTypes,
  RemoveFloorImage,
  RemoveFloorImageComplete,
  SaveFloorPlan,
  SaveFloorPlanFail,
  SaveFloorPlanSuccess,
  UploadFloorImage,
  UploadFloorImageComplete
} from '../actions/floor-plan-editor-view.actions';
import {SaveFloorPlanRequest, UploadFloorImagePayload} from '../models/floor-plan-editor-view';
import {FloorPlanService} from '../../afb-api/services/floor-plan.service';
import {HttpErrorResponse} from '@angular/common/http';
import {EMPTY, of} from 'rxjs';
import {GetOrganizationFloors} from '../../afb-api/actions/floor-plan.actions';

@Injectable()
export class FloorPlanEditorViewEffects {

  @Effect()
  uploadFloorImage$ = this.actions$.pipe(
    ofType(FloorPlanEditorViewActionTypes.UploadFloorImage),
    map((action: UploadFloorImage) => action.payload),
    withLatestFrom(this.floorPlanEditorState$.select(fromFloorPlanEditor.selectedFloorPlanImageUrl)),
    map(([imageWithSource, selectedFloorPlanImageUrl]: [UploadFloorImagePayload, string]) => {
      (<any>window).Appcues && (<any>window).Appcues.track('Uploaded floor plan image');
      (<any>window).Appcues && (<any>window).Appcues.show('-LJE0adLe_8y2-JdlUcA');

      const didFloorImageChange: boolean = !(imageWithSource == null && selectedFloorPlanImageUrl == null);
      return new UploadFloorImageComplete({
        didFloorImageChange,
        imageWithSource
      });
    })
  );

  @Effect()
  removeFloorImage$ = this.actions$.pipe(
    ofType(FloorPlanEditorViewActionTypes.RemoveFloorImage),
    withLatestFrom(this.floorPlanEditorState$.select(fromFloorPlanEditor.selectedFloorPlanImageUrl)),
    map(([action, selectedFloorPlanImageUrl]: [RemoveFloorImage, string]) => {
      const didFloorImageChange: boolean = selectedFloorPlanImageUrl != null;
      return new RemoveFloorImageComplete({
        didFloorImageChange
      });
    })
  );

  @Effect()
  saveFloorPlan$ = this.actions$.pipe(
    ofType(FloorPlanEditorViewActionTypes.SaveFloorPlan),
    map((action: SaveFloorPlan) => action.payload),
    exhaustMap(({didFloorImageChange, didDeviceMarkerEntitiesChange, devices, floorId, organizationToken, uploadedImageWithSource}:
                  SaveFloorPlanRequest) => {
      if (didDeviceMarkerEntitiesChange && didFloorImageChange) {
        return this.floorPlanService.postFloorPlanImage({organizationToken, floorId, image: uploadedImageWithSource.file}).pipe(
          switchMap(() => this.floorPlanService.putFloorDevices({organizationToken, devices, floorId})),
          mergeMap(() => [
            new SaveFloorPlanSuccess(),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new SaveFloorPlanFail({
            errorMessage: error.error.message
          })))
        )
      } else if (didDeviceMarkerEntitiesChange) {
        return this.floorPlanService.putFloorDevices({organizationToken, devices, floorId}).pipe(
          mergeMap(() => [
            new SaveFloorPlanSuccess(),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new SaveFloorPlanFail({
            errorMessage: error.error.message
          })))
        )
      } else if (didFloorImageChange) {
        return this.floorPlanService.postFloorPlanImage({organizationToken, floorId, image: uploadedImageWithSource.file}).pipe(
          mergeMap(() => [
            new SaveFloorPlanSuccess(),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new SaveFloorPlanFail({
            errorMessage: error.error.message
          })))
        )
      } else {
        return EMPTY;
      }
    })
  );


  @Effect({dispatch: false})
  saveFloorPlanSuccess$ = this.actions$.pipe(
    ofType(FloorPlanEditorViewActionTypes.SaveFloorPlanSuccess),
    map((action: SaveFloorPlanSuccess) => {
      (<any>window).Appcues && (<any>window).Appcues.track('Saved floor plan');
      (<any>window).Appcues && (<any>window).Appcues.show('-LJE2kXH6DhA8pE3vr9k');
    })
  );

  constructor(private actions$: Actions,
              private floorPlanService: FloorPlanService,
              private floorPlanEditorState$: Store<fromFloorPlanEditor.State>) {
  }
}
